/**
 * The non-dismissable guardrail gate, and the publication risk note beside it.
 *
 * **The gate is deliberately not a toast, snackbar, banner-with-an-X or anything
 * auto-dismissing.** There is no close control and no internal hidden state. The only ways it
 * leaves the screen are editing the text until it no longer matches any category, or
 * navigating away. Ticking the acknowledgement does *not* hide it — that only satisfies the
 * gate, so the warning stays visible right up to the moment Generate is pressed.
 *
 * Fully controlled: the acknowledgement lives in the page, which is what lets the page wire
 * Generate's `disabled` straight to `flagged && !acknowledged` without reaching in here. Three
 * instances can be on screen at once — the describe brief, the edit instruction and a drafted
 * prompt under review — hence the `id` prop; sharing one would make each card's checkbox label
 * tick whichever box rendered first.
 *
 * The warning copy and the category names both come from the server, never from this file —
 * see `src/guardrail/rules.ts` for why.
 */

import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Alert, AlertTitle, Box, Checkbox, Chip, FormControlLabel, Stack, Typography } from "@mui/material";

import type { GuardrailConfig, GuardrailMatch, PublicationRiskLevel } from "@/guardrail/rules";
import type { PublicationRiskPolicy } from "@/guardrail/rules";

export interface GuardrailGateProps {
  match: GuardrailMatch;
  config: GuardrailConfig | undefined;
  acknowledged: boolean;
  onAcknowledgeChange: (value: boolean) => void;
  /** Distinguishes the checkbox when several gates share a page — usually the case. */
  id: string;
  disabled?: boolean;
}

export function GuardrailGate({
  match,
  config,
  acknowledged,
  onAcknowledgeChange,
  id,
  disabled = false,
}: GuardrailGateProps) {
  // Nothing flagged, or the rules have not arrived — in both cases there is nothing to warn
  // about that this component could state accurately.
  if (!match.flagged || !config) return null;

  return (
    <Alert
      severity="warning"
      variant="outlined"
      role="alert"
      aria-live="polite"
      icon={<WarningAmberOutlined />}
      sx={{ backgroundColor: "rgba(217,119,6,0.08)" }}
    >
      <AlertTitle>Authenticity check</AlertTitle>

      <Stack spacing={2}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {config.warningText}
        </Typography>

        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
            Topics detected in your brief
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mt: 0.5 }}>
            {match.matchedCategories.map((category) => (
              <Chip key={category} size="small" color="warning" label={category} />
            ))}
          </Stack>
        </Box>

        <FormControlLabel
          sx={{ alignItems: "flex-start", m: 0, pt: 1, borderTop: "1px solid rgba(217,119,6,0.3)" }}
          control={
            <Checkbox
              id={id}
              checked={acknowledged}
              disabled={disabled}
              onChange={(event) => onAcknowledgeChange(event.target.checked)}
              sx={{ mt: -1 }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {config.acknowledgementLabel}
            </Typography>
          }
        />
      </Stack>
    </Alert>
  );
}

/**
 * The publication risk assessment, surfaced beside the gate.
 *
 * Renders nothing at "standard" — that means nothing about this brief touches an
 * evidence-sensitive topic at all, and a card saying so on every ordinary brief would train
 * people to ignore the one that matters.
 *
 * **Purely informational**, like the module it displays: this never gates Generate. The
 * acknowledgement above and the evidence-risk reason below still do that.
 */
export function PublicationRiskNote({
  level,
  policy,
}: {
  level: PublicationRiskLevel;
  policy: PublicationRiskPolicy | undefined;
}) {
  if (level === "standard" || !policy) return null;

  const message = policy.messages[level];

  return (
    <Alert
      severity={level === "high" ? "error" : "warning"}
      variant="outlined"
      role="status"
      sx={{ backgroundColor: level === "high" ? "rgba(244,63,94,0.08)" : "rgba(217,119,6,0.08)" }}
    >
      <AlertTitle>
        Publication risk assessment — {policy.labels[level] ?? level}
      </AlertTitle>
      {message ? <Typography variant="body2">{message}</Typography> : null}
    </Alert>
  );
}
